import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Clock, Globe } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

/**
 * ExperienceCard - Unified card component for marketplace experiences
 * Used in: Featured, Discover, Search, You might also like, Operator profile
 * 
 * Props:
 * - service: Service object with id, name, price, images, operator, etc.
 * - featured: Boolean to show "Featured" badge
 * - showDistance: Boolean to show distance (for location-based results)
 */
const ExperienceCard = ({ service, featured = false, showDistance = false }) => {
  const navigate = useNavigate();

  if (!service) return null;

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price || 0);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${minutes}m`;
  };

  const mainImage = service.mainImage || (service.images && service.images[0]);
  const operator = service.operator || {};
  const hasReviews = operator.reviewCount > 0 && operator.rating;

  return (
    <Card
      onClick={() => navigate(`/experience/${service.id}`)}
      className="group overflow-hidden transition-all duration-300 cursor-pointer border-0 rounded-2xl"
      style={{ 
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 25px 50px -12px rgb(0 0 0 / 0.25)';
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      data-testid={`experience-card-${service.id}`}
    >
      {/* Image */}
      <div className="aspect-[4/3] relative overflow-hidden">
        {mainImage ? (
          <img 
            src={mainImage}
            alt={service.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
            <Globe className="w-12 h-12 text-gray-400" />
          </div>
        )}
        
        {/* Featured Badge */}
        {featured && (
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1 shadow-lg">
              <Star className="w-3 h-3 fill-white" />
              Featured
            </span>
          </div>
        )}
      </div>

      <CardContent className="p-4">
        {/* Location + Rating (justify-between) */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" />
            <span>{service.city || 'Location TBD'}</span>
            {showDistance && service.distance && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-green-600">{Math.round(service.distance)} mi</span>
              </>
            )}
          </div>
          {hasReviews && (
            <span className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => {
                const rating = operator.rating || 0;
                const roundedRating = Math.ceil(rating * 2) / 2;
                const filled = star <= Math.floor(roundedRating);
                return (
                  <Star 
                    key={star}
                    className={`w-3 h-3 ${filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                  />
                );
              })}
            </span>
          )}
        </div>

        {/* Service Name */}
        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
          {service.name}
        </h3>

        {/* Operator */}
        {operator.name && (
          <p className="text-sm text-gray-500 mb-3">
            by {operator.name}
          </p>
        )}

        {/* Duration + Price */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="flex items-center gap-1 text-sm text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            {formatDuration(service.durationMinutes)}
          </span>
          <div className="text-right">
            <span className="font-bold text-blue-600">
              {formatPrice(service.price, service.currency)}
            </span>
            {service.priceType === 'per_person' && (
              <span className="text-xs text-gray-500">/person</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExperienceCard;
